import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import {
  Sparkles, Wallet, ShieldCheck, Globe2, Zap, Layers, CheckCircle2,
  ArrowRight, BarChart3, MailCheck, Building2, Menu, X as XIcon,
} from "lucide-react";

/**
 * Marketing landing for Xchangely Pay (the merchant-facing product).
 * Lives at /business and links to /pay (the actual portal).
 *
 * Design system: matches the homepage's dark forest-green palette.
 */
export default function BusinessLanding() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);
  const go = (path) => { setMobileOpen(false); navigate(path); };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="business-page">
      {/* Top bar */}
      <header className="bg-foreground text-white sticky top-0 z-30 border-b border-white/5" data-testid="business-header">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="font-display font-bold text-lg flex items-center gap-2" data-testid="business-logo-link">
            <span className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-emerald-300" />
            </span>
            <span>Xchangely <span className="text-emerald-300">Pay</span></span>
          </Link>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/pay")} variant="ghost"
                    className="hidden sm:inline-flex text-white hover:bg-white/10 rounded-xl text-sm"
                    data-testid="business-sign-in-btn">
              Sign in
            </Button>
            <Button onClick={() => navigate("/pay")}
                    className="hidden sm:inline-flex bg-emerald-500 hover:bg-emerald-600 text-foreground rounded-xl text-sm font-semibold"
                    data-testid="business-signup-btn">
              Create merchant account <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="business-mobile-menu-btn"
              className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl text-white hover:bg-white/10 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile slide-down sheet */}
      <div
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
        className={`sm:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-200 ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <div
        role="dialog" aria-modal="true"
        data-testid="business-mobile-sheet"
        className={`sm:hidden fixed inset-x-0 top-0 z-50 bg-foreground text-white shadow-2xl border-b border-white/10
                     transition-transform duration-300 ease-out
                     ${mobileOpen ? "translate-y-0" : "-translate-y-full"}`}
      >
        <div className="max-w-md mx-auto px-5 pt-4 pb-6">
          <div className="flex items-center justify-between mb-5">
            <Link to="/" onClick={() => setMobileOpen(false)} className="font-display font-bold text-lg flex items-center gap-2">
              <span className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-emerald-300" />
              </span>
              <span>Xchangely <span className="text-emerald-300">Pay</span></span>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              data-testid="business-mobile-close-btn"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-2">
            <Button onClick={() => go("/pay")}
                     className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-foreground rounded-xl font-semibold text-base gap-2"
                     data-testid="business-mobile-signup-btn">
              Create merchant account <ArrowRight className="w-4 h-4" />
            </Button>
            <Button onClick={() => go("/pay")} variant="outline"
                     className="w-full h-12 bg-transparent border-white/20 text-white hover:bg-white/10 rounded-xl text-base"
                     data-testid="business-mobile-signin-btn">
              I already have an account
            </Button>
            <Link to="/" onClick={() => setMobileOpen(false)}
                   className="block text-center mt-3 text-sm text-white/60 hover:text-white py-2"
                   data-testid="business-mobile-home-link">
              ← Back to Xchangely
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-foreground text-white pt-16 pb-24 relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(16,185,129,0.45), transparent 40%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.30), transparent 45%)",
          }}
        />
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-5 gap-10 items-center relative">
          <div className="md:col-span-3">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 font-bold mb-5"
                   data-testid="business-eyebrow">
              <Sparkles className="w-3.5 h-3.5" /> For businesses
            </span>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6"
                 data-testid="business-headline">
              Accept crypto payments<br />
              <span className="text-emerald-300">in three clicks.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-xl mb-8" data-testid="business-subhead">
              Generate a payment link, send it to your customer, get paid in
              USDC on your agreed payout schedule. No coding. No custody
              headaches. No card processor on your bank statement.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate("/pay")}
                       size="lg"
                       className="bg-emerald-500 hover:bg-emerald-600 text-foreground rounded-2xl font-semibold gap-2"
                       data-testid="business-hero-cta">
                Create merchant account <ArrowRight className="w-4 h-4" />
              </Button>
              <Button onClick={() => navigate("/pay")} size="lg" variant="ghost"
                       className="text-white hover:bg-white/10 rounded-2xl gap-2"
                       data-testid="business-hero-signin">
                I already have an account
              </Button>
            </div>
            <div className="flex items-center gap-5 mt-8 text-xs text-white/50">
              <span className="flex items-center gap-1.5" data-testid="business-trust-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> Free to start
              </span>
              <span className="flex items-center gap-1.5" data-testid="business-trust-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> No setup fee
              </span>
              <span className="flex items-center gap-1.5" data-testid="business-trust-3">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> Approved in 1 business day
              </span>
            </div>
          </div>

          {/* Mock checkout card */}
          <div className="md:col-span-2">
            <div className="bg-white text-foreground rounded-3xl p-5 shadow-2xl shadow-emerald-500/10 border border-white/10"
                  data-testid="business-mock-card">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Payment link</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">Live</span>
              </div>
              <div className="text-3xl font-display font-bold">€2,400.00</div>
              <div className="text-xs text-muted-foreground mt-0.5">≈ 2,396.40 USDC</div>
              <div className="border-t border-border my-4" />
              <div className="space-y-2.5 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-mono">alice@buyer.co</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span>Card · iDEAL · SEPA</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Payout</span><span>USDC on schedule</span></div>
              </div>
              <Button className="w-full mt-4 rounded-xl gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-foreground font-semibold"
                       data-testid="business-mock-pay-btn" disabled>
                Pay now (preview)
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why merchants pick us */}
      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-bold">Why merchants pick us</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3 mb-3" data-testid="business-why-title">
              Built for ops, not engineers.
            </h2>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              You shouldn't need a developer to take crypto. Here's everything
              you get the moment you're approved.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="business-feature-grid">
            {[
              {
                icon: Zap,
                title: "No code, no plug-ins",
                body: "Open the portal, fill a wallet + amount, click Generate. Share the link by email, WhatsApp, invoice. That's it.",
              },
              {
                icon: Wallet,
                title: "Paid out in USDC",
                body: "Funds settle through Xchangely Pay and we pay you out in USDC on your agreed schedule — no per-link wallet juggling, no on-chain dust.",
              },
              {
                icon: Globe2,
                title: "Every payment method",
                body: "Card, Apple Pay, Google Pay, iDEAL, Bancontact, EPS, SEPA — your customer picks what works for them.",
              },
              {
                icon: BarChart3,
                title: "Live status, per link",
                body: "Track every link in real time: pending, paid, settled, refunded. Refresh from your dashboard.",
              },
              {
                icon: ShieldCheck,
                title: "KYB-grade onboarding",
                body: "We review every applicant by hand. Approved merchants get a clean ledger and full audit trail.",
              },
              {
                icon: Layers,
                title: "One dashboard, all links",
                body: "Search, filter and reconcile every payment you've ever generated. Export-ready.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="xch-card p-5" data-testid={`business-feature-${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g,'')}`}>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3">
                  <Icon className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="font-display font-bold mb-1.5">{title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-muted/30 border-y border-border">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-12">
            <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-bold">How it works</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3" data-testid="business-how-title">
              From signup to settlement in four steps.
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-5" data-testid="business-how-steps">
            {[
              { n: "1", icon: Building2,  t: "Apply",     d: "Tell us about your business — 30 seconds." },
              { n: "2", icon: MailCheck,  t: "Get approved", d: "We review by hand and email you within a business day." },
              { n: "3", icon: Sparkles,   t: "Generate links", d: "Amount + method → ready-to-share Xchangely Pay link." },
              { n: "4", icon: Wallet,     t: "Get paid",     d: "We collect from the customer and pay you out in USDC on schedule." },
            ].map(({ n, icon: Icon, t, d }) => (
              <div key={n} className="xch-card p-5 relative" data-testid={`business-step-${n}`}>
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-xl bg-foreground text-emerald-300 font-display font-bold flex items-center justify-center text-sm">
                  {n}
                </div>
                <Icon className="w-5 h-5 text-emerald-700 mb-3" />
                <div className="font-display font-bold mb-1.5">{t}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — keep light to avoid commitments */}
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-bold">Pricing</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3 mb-4" data-testid="business-pricing-title">
            Transparent, per-transaction.
          </h2>
          <p className="text-sm text-muted-foreground mb-8">
            No setup fee. No monthly minimum. You pay a flat percentage on each
            successfully settled transaction. We'll share the exact rate when
            we review your application.
          </p>
          <div className="xch-card p-6 inline-block text-left" data-testid="business-pricing-card">
            <div className="flex items-baseline gap-2">
              <span className="font-display font-bold text-5xl text-emerald-700">0%</span>
              <span className="text-sm text-muted-foreground">setup</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">No charge until you take your first payment.</div>
            <div className="border-t border-border my-4" />
            <ul className="text-sm space-y-1.5">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Unlimited payment links</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Real-time status</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700" /> USDC payout on schedule</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700" /> Compliance + audit log</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Big CTA */}
      <section className="bg-foreground text-white py-20" data-testid="business-cta">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4">Ready to take crypto payments?</h2>
          <p className="text-white/70 mb-7">
            Get approved in one business day. Send your first payment link the
            same afternoon.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate("/pay")} size="lg"
                     className="bg-emerald-500 hover:bg-emerald-600 text-foreground rounded-2xl font-semibold gap-2"
                     data-testid="business-cta-primary">
              Create merchant account <ArrowRight className="w-4 h-4" />
            </Button>
            <Button asChild size="lg" variant="ghost"
                     className="text-white hover:bg-white/10 rounded-2xl"
                     data-testid="business-cta-contact">
              <a href="mailto:business@xchangely.io">Talk to sales →</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="py-7 bg-foreground border-t border-white/5 text-xs text-white/40">
        <div className="max-w-6xl mx-auto px-5 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} Xchangely Pay</div>
          <div className="flex gap-4">
            <Link to="/legal/terms" className="hover:text-white/70" data-testid="business-footer-terms">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-white/70" data-testid="business-footer-privacy">Privacy</Link>
            <Link to="/" className="hover:text-white/70" data-testid="business-footer-home">Back to Xchangely</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
